import React from 'react';
import { useUser } from '@gadgetinc/react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../api';
import { hasAnyRole, isInstitutionAdministrator } from '../lib/roleUtils';

// Helper function to check if user has admin privileges
function checkIsAdmin(user) {
  if (!user) return false;
  
  // Special email check for backwards compatibility
  if (user?.email === '74spartan@gmail.com') {
    return true;
  }
  
  const userRoles = user?.roles || user?.roleList || [];
  const hasAdminRole = hasAnyRole(userRoles, ['admin', 'system-admin', 'sysadmin']);
  return hasAdminRole || isInstitutionAdministrator(user);
}

const AdminRequests = () => {
  const user = useUser(api);
  const navigate = useNavigate();

  // Debug logging for roles
  React.useEffect(() => {
    if (user) {
      console.log('Current user roles:', user.roles);
      console.log('User email:', user.email);
      console.log('Role types:', user.roles?.map(role => typeof role));
    }
  }, [user]);

  // Authentication check using helper function
  const isAdmin = checkIsAdmin(user);

  // Redirect if not authenticated
  React.useEffect(() => {
    if (user && !isAdmin) {
      console.log('Access denied - user roles:', user.roles);
      navigate('/sign-in');
    }
  }, [user, isAdmin, navigate]);

  // Loading state
  if (!user) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#111111',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Unauthorized access
  if (!isAdmin) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#111111',
        color: '#ffffff',
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: '#1e1e1e',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '500px',
          width: '100%'
        }}>
          <h2 style={{ color: '#ff4757', marginBottom: '1rem' }}>Access Denied</h2>
          <p style={{ marginBottom: '1.5rem' }}>You need administrator privileges to access this page.</p>
          <p style={{ fontWeight: 'bold', marginBottom: '1rem' }}>Debug Info:</p>
          <div style={{
            background: '#0a0a0a',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            fontSize: '14px'
          }}>
            <p style={{ marginBottom: '0.5rem' }}><strong>Email:</strong> {user?.email || 'Not available'}</p>
            <p style={{ marginBottom: '0.5rem' }}><strong>Roles:</strong> {user?.roles ? JSON.stringify(user.roles, null, 2) : 'No roles'}</p>
            <p><strong>Required:</strong> admin, system-admin roles or email 74spartan@gmail.com</p>
          </div>
          <Link 
            to="/sign-in" 
            style={{
              display: 'inline-block',
              background: '#3b82f6',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.background = '#2563eb'}
            onMouseOut={(e) => e.target.style.background = '#3b82f6'}
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#111111',
      color: '#ffffff',
      padding: '2rem'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        
        {/* Back to Admin Dashboard Button */}
        <div style={{ marginBottom: '2rem' }}>
          <Link 
            to="/admin" 
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: '#3b82f6',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '600',
              fontSize: '16px',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
            onMouseOver={(e) => {
              e.target.style.background = '#2563eb';
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = '#3b82f6';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
          >
            <span style={{ fontSize: '18px' }}>←</span>
            Back to Admin Dashboard
          </Link>
        </div>

        {/* Main Content */}
        <div style={{
          background: '#1e1e1e',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '2rem'
        }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '600',
            marginBottom: '1rem',
            color: '#ffffff'
          }}>Request Management Dashboard</h1>
          
          <p style={{
            fontSize: '16px',
            color: '#d1d5db',
            marginBottom: '2rem'
          }}>
            Coming soon - this page will show pending requests for approval.
          </p>
          
          <div style={{
            background: '#2a2a2a',
            border: '1px solid #404040',
            borderRadius: '8px',
            padding: '1.5rem'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              marginBottom: '1rem',
              color: '#ffffff'
            }}>Request Types:</h3>
            <ul style={{
              listStyle: 'none',
              padding: '0',
              margin: '0',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem'
            }}>
              <li style={{
                background: '#3a3a3a',
                padding: '1rem',
                borderRadius: '6px',
                color: '#d1d5db',
                border: '1px solid #505050'
              }}>
                <strong style={{ color: '#ffffff' }}>Preceptor Requests</strong> - New preceptor applications and approvals
              </li>
              <li style={{
                background: '#3a3a3a',
                padding: '1rem',
                borderRadius: '6px',
                color: '#d1d5db',
                border: '1px solid #505050'
              }}>
                <strong style={{ color: '#ffffff' }}>User Approvals</strong> - Student and instructor account approvals
              </li>
              <li style={{
                background: '#3a3a3a',
                padding: '1rem',
                borderRadius: '6px',
                color: '#d1d5db',
                border: '1px solid #505050'
              }}>
                <strong style={{ color: '#ffffff' }}>Shift Change Requests</strong> - Schedule modifications and cancellations
              </li>
              <li style={{
                background: '#3a3a3a',
                padding: '1rem',
                borderRadius: '6px',
                color: '#d1d5db',
                border: '1px solid #505050'
              }}>
                <strong style={{ color: '#ffffff' }}>Audit Flags</strong> - Items requiring administrative review
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminRequests;
